import { motion } from 'framer-motion';
import {
  Globe,
  TrendingUp,
  Building2,
  Users,
  Scale,
  Leaf,
  ArrowRight,
  Map,
  Lightbulb,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function PestelAnalysisPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const factors = [
    {
      letter: 'P',
      name: 'Political',
      icon: Scale,
      description:
        'Government policies, political stability, and regulatory environment that impact business operations and strategy',
      examples: [
        'Government stability and ideology',
        'Tax policy and incentives',
        'Trade restrictions and tariffs',
        'Labor and employment laws',
        'Political risk in markets',
      ],
      questions: [
        'How stable is the political environment?',
        'What regulatory changes are coming?',
        'How do trade policies affect us?',
        'What is our relationship with regulators?',
      ],
    },
    {
      letter: 'E',
      name: 'Economic',
      icon: TrendingUp,
      description:
        'Macro-economic factors that influence consumer spending, business investment, and overall market conditions',
      examples: [
        'GDP growth rates',
        'Interest and inflation rates',
        'Exchange rate fluctuations',
        'Unemployment levels',
        'Consumer confidence indices',
      ],
      questions: [
        'Is the economy expanding or contracting?',
        'How do interest rates affect our costs?',
        "What's happening with currency exchange?",
        'Can customers afford our products?',
      ],
    },
    {
      letter: 'S',
      name: 'Social',
      icon: Users,
      description:
        'Demographic trends, cultural attitudes, and social factors that shape customer behavior and workforce dynamics',
      examples: [
        'Demographic shifts (aging, urbanization)',
        'Cultural trends and values',
        'Education levels',
        'Lifestyle and health consciousness',
        'Remote work adoption',
      ],
      questions: [
        'How are demographics changing?',
        'What cultural trends matter?',
        'How are customer preferences evolving?',
        'What social movements affect us?',
      ],
    },
    {
      letter: 'T',
      name: 'Technological',
      icon: Globe,
      description:
        'Technological innovation, automation, and digital transformation reshaping industries and business models',
      examples: [
        'AI and automation advances',
        'R&D investment and innovation',
        'Technology infrastructure',
        'Cybersecurity threats',
        'Emerging technologies (blockchain, quantum)',
      ],
      questions: [
        'What technologies could disrupt us?',
        'How is AI changing our industry?',
        'Are we investing enough in R&D?',
        'What cyber risks do we face?',
      ],
    },
    {
      letter: 'E',
      name: 'Environmental',
      icon: Leaf,
      description:
        'Climate change, sustainability requirements, and environmental regulations impacting operations and brand reputation',
      examples: [
        'Climate change impacts',
        'Carbon regulations and emissions',
        'Sustainability expectations',
        'Resource scarcity',
        'Circular economy trends',
      ],
      questions: [
        "What's our carbon footprint?",
        'What environmental regulations are coming?',
        'How do stakeholders view our sustainability?',
        'Are key resources at risk?',
      ],
    },
    {
      letter: 'L',
      name: 'Legal',
      icon: Building2,
      description:
        'Laws, regulations, and legal frameworks governing business operations, contracts, and intellectual property',
      examples: [
        'Employment and labor law',
        'Consumer protection regulations',
        'Intellectual property rights',
        'Data privacy (GDPR, CCPA)',
        'Antitrust and competition law',
      ],
      questions: [
        'Are we compliant with all regulations?',
        'What legal changes are pending?',
        'How protected is our IP?',
        'What liability risks exist?',
      ],
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'External Assessment', path: '/external-assessment' },
              { label: 'PESTEL Analysis' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Map className="w-4 h-4 mr-2" />
            External Assessment Tool 2 of 4
          </span>
        }
        title="Spot the changes your strategy must address"
        subtitle="Look beyond your company with PESTEL analysis. Connect political, economic, social, technological, environmental, and legal changes to the opportunities and risks that matter to you."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Scan your environment
        </Link>
      </PageHero>

      {/* Six Factors */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Look beyond the business before you commit</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A change in the wider environment can reshape a sound plan. Use these six lenses to
              ask what is changing, why it matters, and what evidence supports your view.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {factors.map((f, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-6 rounded-xl border border-gray-200"
              >
                <div className="bg-blue-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <f.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{f.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{f.description}</p>
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">Key Areas:</h4>
                  <ul className="space-y-1">
                    {f.examples.map((ex, j) => (
                      <li key={j} className="text-xs text-gray-600 flex items-start">
                        <span className="text-blue-500 mr-1.5">•</span>
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-3 border-t border-blue-100">
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">Questions:</h4>
                  <ul className="space-y-1">
                    {f.questions.map((q, j) => (
                      <li key={j} className="text-xs text-gray-600 flex items-start">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Conduct */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Conduct PESTEL Analysis</h2>
              <p className="text-xl text-gray-600">
                A systematic approach to environmental scanning
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Brainstorm Each Factor',
                  description:
                    "Work through each PESTEL category systematically. Gather your team and list all relevant factors that could impact your business. Don't self-censor—capture everything initially.",
                  tip: 'Use diverse perspectives (sales, operations, finance) since different functions spot different environmental factors.',
                },
                {
                  step: '2',
                  title: 'Research and Validate',
                  description:
                    'Back up brainstormed factors with data. Use industry reports, government statistics, trend analyses, and expert forecasts. Distinguish between real trends and temporary noise.',
                  tip: 'Set up Google Alerts or industry monitoring tools for ongoing environmental scanning rather than one-time research.',
                },
                {
                  step: '3',
                  title: 'Assess Impact and Likelihood',
                  description:
                    'Not all factors matter equally. Rate each on two dimensions: (1) How much impact would it have on your business? (2) How likely is it to occur? Focus on high-impact, high-likelihood factors.',
                  tip: 'Use a 2x2 matrix: High Impact + High Likelihood = Priority. High Impact + Low Likelihood = Monitor. Low Impact = Deprioritize.',
                },
                {
                  step: '4',
                  title: 'Identify Opportunities and Threats',
                  description:
                    'For each significant factor, determine: Is this an opportunity we can exploit or a threat we must defend against? Some factors create both (e.g., AI automation).',
                  tip: 'Be specific about the opportunity or threat mechanism. Don\'t just say "AI is an opportunity"—explain how you\'ll leverage it.',
                },
                {
                  step: '5',
                  title: 'Develop Strategic Responses',
                  description:
                    "For key opportunities, define how you'll capitalize. For key threats, define how you'll mitigate or avoid them. Assign owners and timelines. Link to your strategic initiatives.",
                  tip: 'Some environmental factors require immediate action. Others are "watch and wait." Be explicit about your response timing and triggers.',
                },
              ].map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                      <p className="text-gray-700 mb-3">{step.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {step.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Example */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Example: Electric Vehicle Industry (2026)</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              How PESTEL factors shape strategy in a rapidly evolving industry
            </p>
          </motion.div>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  factor: 'Political',
                  impact: 'High Positive',
                  items: [
                    'Government EV subsidies and tax credits',
                    'Infrastructure investment in charging networks',
                    'ICE vehicle phase-out mandates in EU, California',
                    'Trade policies affecting battery supply chains',
                  ],
                },
                {
                  factor: 'Economic',
                  impact: 'Mixed',
                  items: [
                    'Battery costs declining 10% annually',
                    'Interest rates affecting financing and capex',
                    'Total cost of ownership favoring EVs in many markets',
                    'Economic uncertainty impacting luxury EV sales',
                  ],
                },
                {
                  factor: 'Social',
                  impact: 'High Positive',
                  items: [
                    'Growing climate consciousness among consumers',
                    'Younger demographics preferring sustainable brands',
                    'Range anxiety decreasing with infrastructure',
                    'Social status associated with Tesla, Rivian, etc.',
                  ],
                },
                {
                  factor: 'Technological',
                  impact: 'High Positive',
                  items: [
                    'Solid-state battery breakthroughs coming 2027-2028',
                    'Autonomous driving integration with EVs',
                    'Charging speed improvements (80% in 15 min)',
                    'Manufacturing automation reducing costs',
                  ],
                },
                {
                  factor: 'Environmental',
                  impact: 'Very High Positive',
                  items: [
                    'Carbon emission regulations tightening globally',
                    'Corporate ESG commitments driving fleet electrification',
                    'Circular economy focus on battery recycling',
                    'Supply chain pressure to reduce embodied carbon',
                  ],
                },
                {
                  factor: 'Legal',
                  impact: 'Medium Mixed',
                  items: [
                    'Safety regulations evolving for battery tech',
                    'Right-to-repair legislation affecting service models',
                    'Data privacy concerns with connected vehicles',
                    'Product liability for autonomous features',
                  ],
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">{item.factor}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${item.impact.includes('Positive') ? 'bg-green-100 text-green-700' : item.impact === 'Mixed' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}
                    >
                      {item.impact}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {item.items.map((bullet, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start">
                        <CheckCircle className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card mt-8 p-6 bg-blue-50 rounded-xl border border-gray-200"
            >
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-blue-600" />
                Strategic Implication:
              </h4>
              <p className="text-gray-700 leading-relaxed">
                The EV industry faces overwhelmingly favorable macro conditions across multiple
                PESTEL dimensions—political support, environmental pressure, technology maturity,
                and social acceptance all align. The primary strategic challenge is execution:
                scaling production, securing battery supply chains, and building charging
                infrastructure fast enough to meet demand. Economic sensitivity remains (premium
                pricing limits mass-market penetration), and legal/safety frameworks are still
                evolving. Winners will be those who move fastest while managing supply chain and
                regulatory complexity.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common PESTEL Mistakes</h2>
              <p className="text-xl text-gray-600">
                Avoid these pitfalls when analyzing your macro environment
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Listing Without Prioritizing',
                  description:
                    'Creating exhaustive lists of factors without assessing which ones actually matter. You end up with 50 factors and no clear priorities.',
                  fix: 'Force rank factors by impact and likelihood. Focus on the top 10-15 factors that could significantly affect your strategy.',
                },
                {
                  mistake: 'Analysis Paralysis',
                  description:
                    'Spending weeks researching every possible environmental factor while markets move and competitors act. Perfect analysis beats timely insight.',
                  fix: 'Time-box PESTEL to 2-3 weeks. Use 80/20 rule: capture 80% of insights with 20% of possible research time.',
                },
                {
                  mistake: 'Static Snapshot',
                  description:
                    'Treating PESTEL as a one-time strategic planning exercise rather than ongoing environmental monitoring.',
                  fix: 'Build environmental scanning into quarterly strategy reviews. Assign factor "owners" who monitor specific areas continuously.',
                },
                {
                  mistake: 'Missing Interconnections',
                  description:
                    'Analyzing each PESTEL factor in isolation when they often interact. Political changes drive legal changes. Economic conditions affect social trends.',
                  fix: 'Map relationships between factors. A political shift toward protectionism has economic (tariffs), legal (trade law), and social (nationalism) implications.',
                },
                {
                  mistake: 'Local Bias',
                  description:
                    "Only considering your home market when environmental factors vary dramatically by geography. What's true in the US may not apply in India or Germany.",
                  fix: 'Run separate PESTEL analyses for strategically important geographic markets. Some factors are global (climate), others local (regulations).',
                },
                {
                  mistake: 'No Strategic Translation',
                  description:
                    'Documenting environmental factors without defining what they mean for your strategy. Analysis without application is wasted effort.',
                  fix: 'For each high-priority factor, explicitly state: Is this an opportunity or threat? What specific actions will we take? By when?',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">Bring external change into your planning</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Gather the signals</h3>
                <p className="text-gray-600">
                  Capture relevant changes and their sources across the six PESTEL categories.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Assess the implications</h3>
                <p className="text-gray-600">
                  Explain how each factor could affect demand, costs, operations, or your ability to
                  compete.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Decide what to revisit</h3>
                <p className="text-gray-600">
                  Bring significant risks and opportunities into your objectives, scenarios, and
                  initiatives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/external-assessment/porters-five-forces"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-blue-600">Porter's Five Forces</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Complement macro analysis with industry-specific competitive forces
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment/swot-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-purple-600">SWOT Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Convert PESTEL opportunities and threats into actionable SWOT insights
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/growth-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Growth Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Use environmental trends to identify high-potential growth opportunities
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/external-assessment/porters-five-forces"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Porter's Five Forces
              </Link>
              <Link
                to="/external-assessment/market-analysis"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Market Analysis <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Scan your environment in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Bring significant risks and opportunities into your objectives, scenarios, and
            initiatives. Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Scan your environment <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
